/**
 * The web app's side of the song schema: V4 in memory and on the wire.
 *
 * Everything the app writes for someone else — the .chordbaker download and
 * the transfer code posted to Buffet — goes through `toWireSong`, which
 * converts the in-memory V4 song to `SCHEMA_WRITE_VERSION` (4 since the
 * 2026-10-18 flip; it was 3 while app versions that crash on V4 were in the
 * field, and never dropped data). Everything the app
 * reads goes through `upgradeImportedSong`. Rules and rollout:
 * packages/music-theory/docs/schema-versions.md.
 */
import {
  convertStateToVersion,
  detectVersion,
  migrateStateV3ToV4,
  resolveWriteVersion,
  SCHEMA_VERSION_LATEST,
  UnsupportedSchemaVersionError,
  validateAndNormalizeStateV3,
  validateAndNormalizeStateV4,
  type ChordCakeStateV3OrV4,
  type WritableSchemaVersion,
} from '@chordbaker/music-theory';
import type { ChordCakeState } from '../types';

/** The schema version written to files and Buffet. 4 since the flip; set back to 3 only to re-support pre-3.3.4 readers. */
export const SCHEMA_WRITE_VERSION: WritableSchemaVersion = 4;

/** Shown when a file or chart was made by a newer ChordBaker than this one. */
export const NEWER_VERSION_MESSAGE = 'This song was made with a newer version of ChordBaker. Update the app to open it.';

/** The song in the shape to write: the configured version unless that would lose data. */
export const toWireSong = (song: ChordCakeState): ChordCakeStateV3OrV4 =>
  convertStateToVersion(song, resolveWriteVersion(song, SCHEMA_WRITE_VERSION));

/**
 * A V3 or V4 song (a file, a Buffet chart, a transfer) validated and
 * brought to the app's V4 state. V1/V2 are handled by the caller first.
 * Throws `UnsupportedSchemaVersionError` for a newer version.
 */
export const upgradeImportedSong = (data: unknown): ChordCakeState => {
  const version = detectVersion(data);
  if (version > SCHEMA_VERSION_LATEST) {
    throw new UnsupportedSchemaVersionError(version);
  }
  if (version === 4) {
    validateAndNormalizeStateV4(data);
    return data as ChordCakeState;
  }
  validateAndNormalizeStateV3(data);
  return migrateStateV3ToV4(data);
};
